import React, { memo, useCallback, useState, useMemo } from 'react';
import { Button, Text } from '@Sage/hoi-poi-ui';
import { getLiteral } from 'utils/getLiteral';
import colors from 'constants/colors';
import senderIdentityImg from '../assets/sender-identity.webp';
import manageSubscribersImg from '../assets/manage-subscribers.webp';
import SenderFormModal from '../SenderFormModal';
import SetupStatusIcon from '../components/SetupStatusIcon';
import { useInitialSetupCheck } from '../hooks/InitialSetupCheckContext';
import { useNavigate } from 'react-router-dom';

import './styles.scss';

const SetupStep = memo(
    ({ isConfigured, title, description, actionLabel, onAction, illustration }) => {
        return (
            <div className="marketing-initial-setup__section">
                <div className="marketing-initial-setup__step-icon">
                    <SetupStatusIcon isCompleted={isConfigured} />
                </div>
                <div className="marketing-initial-setup__content">
                    <Text type="h6" className="marketing-initial-setup__title">
                        {title}
                    </Text>
                    <Text type="body" className="marketing-initial-setup__description">
                        {description}
                    </Text>
                    <Button type="secondary" onClick={onAction} isDisabled={isConfigured}>
                        {actionLabel}
                    </Button>
                </div>
                <div className="marketing-initial-setup__illustration">
                    {illustration ? (
                        <img
                            src={illustration}
                            alt=""
                            className="marketing-initial-setup__illustration-img"
                        />
                    ) : (
                        <div className="marketing-initial-setup__illustration-placeholder" />
                    )}
                </div>
            </div>
        );
    },
);

SetupStep.displayName = 'SetupStep';

const InitialSetup = memo(({ onManageSubscribers }) => {
    const [isSenderModalOpen, setIsSenderModalOpen] = useState(false);
    const [createdSender, setCreatedSender] = useState(false);
    const { hasMinimumSenders, refetchSenders } = useInitialSetupCheck();
    const navigate = useNavigate();
    const handleConfigureSender = useCallback(() => {
        setIsSenderModalOpen(true);
    }, []);

    const handleManageSubscribers = useCallback(() => {
        onManageSubscribers();
    }, [onManageSubscribers]);

    const handleCloseSenderModal = useCallback(() => {
        setIsSenderModalOpen(false);
    }, []);

    const handleSenderSuccess = useCallback(() => {
        setCreatedSender(true);
        void refetchSenders({ force: true });
    }, [refetchSenders]);

    const handleCreateCampaign = useCallback(() => {
        navigate('/campaigns/marketing/new');
    }, [navigate]);

    const isSenderConfigured = useMemo(() => {
        return createdSender || hasMinimumSenders;
    }, [createdSender, hasMinimumSenders]);

    return (
        <div className="marketing-initial-setup">
            <div className="marketing-initial-setup__page-header">
                <Button
                    type="primary"
                    isDisabled={!isSenderConfigured}
                    onClick={handleCreateCampaign}
                >
                    {getLiteral('action_create_campaign')}
                </Button>
            </div>
            <div className="marketing-initial-setup__container">
                <Text medium color={colors.grey[600]} className="marketing-initial-setup__header">
                    {getLiteral('label_how_to_start_sending_emails')}
                </Text>
                <div className="marketing-initial-setup__content-wrapper">
                    <SetupStep
                        isConfigured={isSenderConfigured}
                        title={getLiteral(
                            'label_marketing_campaigns_configure_your_sender_identity',
                        )}
                        description={getLiteral(
                            'label_marketing_campaigns_configure_sender_identity_desc',
                        )}
                        actionLabel={getLiteral(
                            'action_marketing_campaigns_configure_sender_identity',
                        )}
                        onAction={handleConfigureSender}
                        illustration={senderIdentityImg}
                    />

                    <SetupStep
                        isConfigured={false}
                        title={getLiteral('label_marketing_campaigns_mark_subscribers_title')}
                        description={getLiteral('label_marketing_campaigns_mark_subscribers_desc')}
                        actionLabel={getLiteral('action_marketing_campaigns_manage_subscribers')}
                        onAction={handleManageSubscribers}
                        illustration={manageSubscribersImg}
                    />
                </div>
            </div>
            <SenderFormModal
                isOpen={isSenderModalOpen}
                onCancel={handleCloseSenderModal}
                onRequestClose={handleCloseSenderModal}
                onSuccess={handleSenderSuccess}
            />
        </div>
    );
});

InitialSetup.displayName = 'InitialSetup';

export default InitialSetup;
